import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  SimpleGrid,
  Select,
  FormControl,
  FormLabel,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  useToast,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';

import { formatDateTime } from 'utils/date.util';
import RunProgressPanel from 'components/run-progress/RunProgressPanel';
import { mapRunStepsToUi } from 'components/run-progress/mapRunStepsToUi';
import socketStorePolicies from 'app/socketStorePolicies';

import { useColors } from './colors';
import {
  getStorePoliciesApi,
  refreshStorePoliciesApi,
  getPoliciesProcessStatusApi,
  getPoliciesRefreshStatusApi,
  updateStoreDefaultPoliciesApi,
  startPoliciesBulkUpdateApi,
  stopPoliciesBulkUpdateApi,
  downloadPoliciesRunReportCsvApi,
} from 'features/stores/stores.api';
import {
  setPoliciesProcessStatusFromSocket,
  setPoliciesRefreshStatusFromSocket,
} from 'features/stores/stores.slice';

const POLICY_SUMMARY_STATS = [
  { key: 'total', label: 'Total', color: undefined },
  { key: 'success', label: 'Success', color: 'green.500' },
  { key: 'failed', label: 'Failed', color: 'red.500' },
];

const formatListingCount = (count) => (count ?? 0).toLocaleString();

const PolicyTable = ({ title, policies, textColor, borderColor, headerBg }) => (
  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden">
    <Text fontWeight="semibold" px={4} py={3} bg={headerBg} color={textColor}>
      {title}
    </Text>
    <Table size="sm" variant="simple">
      <Thead>
        <Tr>
          <Th>Name</Th>
          <Th isNumeric>Listings</Th>
        </Tr>
      </Thead>
      <Tbody>
        {(policies || []).length === 0 ? (
          <Tr>
            <Td colSpan={2} color="gray.500">
              No policies loaded. Click Refresh to fetch from eBay.
            </Td>
          </Tr>
        ) : (
          policies.map((p) => (
            <Tr key={p.id}>
              <Td maxW="280px" isTruncated title={p.name}>
                {p.name}
              </Td>
              <Td isNumeric>{formatListingCount(p.listingCount)}</Td>
            </Tr>
          ))
        )}
      </Tbody>
    </Table>
  </Box>
);

const StorePoliciesTab = ({ storeId }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { textColor, cardBgColor, borderColor, inputBgColor, inputColor, headingBgColor } =
    useColors();

  const {
    storePolicies,
    getStorePoliciesStatus,
    refreshStorePoliciesStatus,
    policiesProcessStatus,
    policiesRefreshStatus,
    isPoliciesRunning,
    isPoliciesRefreshing,
    updateDefaultPoliciesStatus,
    downloadPoliciesReportStatus,
  } = useSelector((state) => state.stores);

  const [defaultFulfillmentId, setDefaultFulfillmentId] = useState('');
  const [defaultPaymentId, setDefaultPaymentId] = useState('');
  const [defaultReturnId, setDefaultReturnId] = useState('');

  const [fulfillmentPolicyId, setFulfillmentPolicyId] = useState('');
  const [paymentPolicyId, setPaymentPolicyId] = useState('');
  const [returnPolicyId, setReturnPolicyId] = useState('');

  const steps = useMemo(() => mapRunStepsToUi(policiesProcessStatus), [policiesProcessStatus]);
  const overallProgress = policiesProcessStatus?.overallProgress ?? 0;
  const displayIsRunning = isPoliciesRunning || Boolean(policiesProcessStatus?.isRunning);
  const runSummary = policiesProcessStatus?.runSummary ?? null;
  const hasReport = Boolean(policiesProcessStatus?.hasReport);

  const refreshProgress = policiesRefreshStatus?.overallProgress ?? 0;
  const displayIsRefreshing =
    isPoliciesRefreshing || Boolean(policiesRefreshStatus?.isRunning);
  const refreshMessage = policiesRefreshStatus?.message ?? '';

  const reloadPolicies = () => {
    if (storeId) dispatch(getStorePoliciesApi(storeId));
  };

  useEffect(() => {
    if (!storeId) return;
    reloadPolicies();
    dispatch(getPoliciesProcessStatusApi(storeId));
    dispatch(getPoliciesRefreshStatusApi(storeId));
  }, [dispatch, storeId]);

  useEffect(() => {
    if (!storeId) return undefined;

    socketStorePolicies.connect();
    const onRunState = (payload) => {
      if (payload?.storeId && payload.storeId !== storeId) return;
      dispatch(setPoliciesProcessStatusFromSocket(payload));
    };
    const onRefreshState = (payload) => {
      if (payload?.storeId && payload.storeId !== storeId) return;
      dispatch(setPoliciesRefreshStatusFromSocket(payload));
    };
    const onRefreshComplete = (payload) => {
      if (payload?.storeId && payload.storeId !== storeId) return;
      reloadPolicies();
    };
    const onStatus = (payload) => {
      if (payload?.storeId && payload.storeId !== storeId) return;
      dispatch(getPoliciesProcessStatusApi(storeId));
    };

    socketStorePolicies.on('policies-run-state', onRunState);
    socketStorePolicies.on('policies-refresh-state', onRefreshState);
    socketStorePolicies.on('policies-refresh-complete', onRefreshComplete);
    socketStorePolicies.on('policies-status', onStatus);

    return () => {
      socketStorePolicies.off('policies-run-state', onRunState);
      socketStorePolicies.off('policies-refresh-state', onRefreshState);
      socketStorePolicies.off('policies-refresh-complete', onRefreshComplete);
      socketStorePolicies.off('policies-status', onStatus);
      socketStorePolicies.disconnect();
    };
  }, [dispatch, storeId]);

  useEffect(() => {
    if (!displayIsRunning || !storeId) return undefined;
    const interval = setInterval(() => {
      dispatch(getPoliciesProcessStatusApi(storeId));
    }, 2000);
    return () => clearInterval(interval);
  }, [dispatch, storeId, displayIsRunning]);

  useEffect(() => {
    if (!displayIsRefreshing || !storeId) return undefined;
    const interval = setInterval(() => {
      dispatch(getPoliciesRefreshStatusApi(storeId));
    }, 2000);
    return () => clearInterval(interval);
  }, [dispatch, storeId, displayIsRefreshing]);

  useEffect(() => {
    if (!displayIsRefreshing && policiesRefreshStatus?.overallProgress === 100) {
      reloadPolicies();
    }
  }, [displayIsRefreshing, policiesRefreshStatus?.overallProgress, storeId]);

  useEffect(() => {
    const defaults = storePolicies?.defaultPolicies;
    if (!defaults) return;
    setDefaultFulfillmentId(defaults.fulfillmentPolicyId ?? '');
    setDefaultPaymentId(defaults.paymentPolicyId ?? '');
    setDefaultReturnId(defaults.returnPolicyId ?? '');
  }, [storePolicies?.defaultPolicies]);

  const savedDefaults = storePolicies?.defaultPolicies;
  const hasSavedDefaults = Boolean(
    savedDefaults?.fulfillmentPolicyId &&
      savedDefaults?.paymentPolicyId &&
      savedDefaults?.returnPolicyId,
  );

  const handleSaveDefaults = async () => {
    if (!defaultFulfillmentId || !defaultPaymentId || !defaultReturnId) {
      toast({
        title: 'Select all three default policies',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    await dispatch(
      updateStoreDefaultPoliciesApi({
        storeId,
        fulfillmentPolicyId: defaultFulfillmentId,
        paymentPolicyId: defaultPaymentId,
        returnPolicyId: defaultReturnId,
      }),
    );
  };

  const applyDefaultsToBulk = () => {
    if (!defaultFulfillmentId || !defaultPaymentId || !defaultReturnId) {
      toast({
        title: 'Save default policies first',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setFulfillmentPolicyId(defaultFulfillmentId);
    setPaymentPolicyId(defaultPaymentId);
    setReturnPolicyId(defaultReturnId);
  };

  const handleRefresh = async () => {
    const result = await dispatch(refreshStorePoliciesApi(storeId));
    if (refreshStorePoliciesApi.rejected.match(result)) {
      toast({
        title: 'Refresh failed',
        description: result.payload?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } else {
      dispatch(getPoliciesRefreshStatusApi(storeId));
    }
  };

  const handleStartBulk = async () => {
    if (!fulfillmentPolicyId || !paymentPolicyId || !returnPolicyId) {
      toast({
        title: 'Select all three policies',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const result = await dispatch(
      startPoliciesBulkUpdateApi({
        storeId,
        fulfillmentPolicyId,
        paymentPolicyId,
        returnPolicyId,
      }),
    );
    if (startPoliciesBulkUpdateApi.rejected.match(result)) {
      toast({
        title: 'Could not start bulk update',
        description: result.payload?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } else {
      dispatch(getPoliciesProcessStatusApi(storeId));
    }
  };

  const handleStop = () => {
    dispatch(stopPoliciesBulkUpdateApi(storeId));
  };

  const isLoading = getStorePoliciesStatus === 'pending';
  const isStartingRefresh = refreshStorePoliciesStatus === 'pending';

  return (
    <VStack align="stretch" spacing={6}>
      <Box bg={cardBgColor} borderRadius="xl" p={6}>
        <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3}>
          <VStack align="start" spacing={1}>
            <Text fontSize="lg" fontWeight="semibold" color={textColor}>
              eBay account policies
            </Text>
            {storePolicies?.lastFetchTime && (
              <Text fontSize="sm" color="gray.500">
                Last fetched: {formatDateTime(storePolicies.lastFetchTime)}
              </Text>
            )}
          </VStack>
          <HStack spacing={3} flexWrap="wrap">
            <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full">
              {(storePolicies?.totalListings ?? 0).toLocaleString()} store listings
            </Badge>
            {displayIsRefreshing && (
              <Badge colorScheme="orange" fontSize="md" px={3} py={1} borderRadius="full">
                Refresh {refreshProgress}%
              </Badge>
            )}
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="blue"
              variant="outline"
              onClick={handleRefresh}
              isLoading={isStartingRefresh}
              loadingText="Starting…"
              isDisabled={isLoading || displayIsRefreshing || displayIsRunning}
            >
              Refresh policies
            </Button>
          </HStack>
        </HStack>

        {displayIsRefreshing && (
          <Box mb={4}>
            <Progress
              value={refreshProgress}
              size="sm"
              colorScheme="orange"
              borderRadius="full"
              hasStripe
              isAnimated
            />
            {refreshMessage && (
              <Text fontSize="sm" color="gray.500" mt={2}>
                {refreshMessage}
              </Text>
            )}
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
          <PolicyTable
            title="Fulfillment"
            policies={storePolicies?.fulfillmentPolicies}
            textColor={textColor}
            borderColor={borderColor}
            headerBg={headingBgColor}
          />
          <PolicyTable
            title="Payment"
            policies={storePolicies?.paymentPolicies}
            textColor={textColor}
            borderColor={borderColor}
            headerBg={headingBgColor}
          />
          <PolicyTable
            title="Return"
            policies={storePolicies?.returnPolicies}
            textColor={textColor}
            borderColor={borderColor}
            headerBg={headingBgColor}
          />
        </SimpleGrid>
      </Box>

      <Box bg={cardBgColor} borderRadius="xl" p={6}>
        <HStack justify="space-between" mb={2} flexWrap="wrap" gap={3}>
          <VStack align="start" spacing={1}>
            <Text fontSize="lg" fontWeight="semibold" color={textColor}>
              Default store policies
            </Text>
            <Text fontSize="sm" color="gray.500">
              Saved on this store and used by listing creation and other automated flows.
            </Text>
          </VStack>
          {hasSavedDefaults && (
            <Badge colorScheme="green" px={3} py={1} borderRadius="full">
              Defaults configured
            </Badge>
          )}
        </HStack>

        {hasSavedDefaults && (
          <Box mb={4} p={3} bg={headingBgColor} borderRadius="md">
            <Text fontSize="sm" color={textColor}>
              <strong>Current:</strong>{' '}
              {savedDefaults.fulfillmentPolicyName || 'Fulfillment'} ·{' '}
              {savedDefaults.paymentPolicyName || 'Payment'} ·{' '}
              {savedDefaults.returnPolicyName || 'Return'}
            </Text>
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
          <FormControl>
            <FormLabel color={textColor}>Default fulfillment</FormLabel>
            <Select
              placeholder="Select fulfillment"
              value={defaultFulfillmentId}
              onChange={(e) => setDefaultFulfillmentId(e.target.value)}
              bg={inputBgColor}
              color={inputColor}
              isDisabled={!(storePolicies?.fulfillmentPolicies?.length > 0)}
            >
              {(storePolicies?.fulfillmentPolicies || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.listingCount ?? 0})
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel color={textColor}>Default payment</FormLabel>
            <Select
              placeholder="Select payment"
              value={defaultPaymentId}
              onChange={(e) => setDefaultPaymentId(e.target.value)}
              bg={inputBgColor}
              color={inputColor}
              isDisabled={!(storePolicies?.paymentPolicies?.length > 0)}
            >
              {(storePolicies?.paymentPolicies || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.listingCount ?? 0})
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel color={textColor}>Default return</FormLabel>
            <Select
              placeholder="Select return"
              value={defaultReturnId}
              onChange={(e) => setDefaultReturnId(e.target.value)}
              bg={inputBgColor}
              color={inputColor}
              isDisabled={!(storePolicies?.returnPolicies?.length > 0)}
            >
              {(storePolicies?.returnPolicies || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.listingCount ?? 0})
                </option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>

        <HStack spacing={3} flexWrap="wrap">
          <Button
            colorScheme="blue"
            onClick={handleSaveDefaults}
            isLoading={updateDefaultPoliciesStatus === 'pending'}
            isDisabled={!(storePolicies?.fulfillmentPolicies?.length > 0)}
          >
            Save defaults
          </Button>
          <Button variant="outline" onClick={applyDefaultsToBulk}>
            Use for bulk update
          </Button>
        </HStack>
      </Box>

      <Box bg={cardBgColor} borderRadius="xl" p={6}>
        <Text fontSize="lg" fontWeight="semibold" color={textColor} mb={4}>
          Bulk policy reassignment
        </Text>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Apply the selected fulfillment, payment, and return policies to all active listings for
          this store. Counts refresh automatically when the update finishes.
        </Text>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
          <FormControl>
            <FormLabel color={textColor}>Fulfillment policy</FormLabel>
            <Select
              placeholder="Select fulfillment"
              value={fulfillmentPolicyId}
              onChange={(e) => setFulfillmentPolicyId(e.target.value)}
              bg={inputBgColor}
              color={inputColor}
            >
              {(storePolicies?.fulfillmentPolicies || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.listingCount ?? 0})
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel color={textColor}>Payment policy</FormLabel>
            <Select
              placeholder="Select payment"
              value={paymentPolicyId}
              onChange={(e) => setPaymentPolicyId(e.target.value)}
              bg={inputBgColor}
              color={inputColor}
            >
              {(storePolicies?.paymentPolicies || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.listingCount ?? 0})
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel color={textColor}>Return policy</FormLabel>
            <Select
              placeholder="Select return"
              value={returnPolicyId}
              onChange={(e) => setReturnPolicyId(e.target.value)}
              bg={inputBgColor}
              color={inputColor}
            >
              {(storePolicies?.returnPolicies || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.listingCount ?? 0})
                </option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>
        <HStack spacing={3}>
          <Button
            colorScheme="teal"
            onClick={handleStartBulk}
            isLoading={displayIsRunning}
            isDisabled={displayIsRunning || displayIsRefreshing}
          >
            Start bulk update
          </Button>
          <Button
            variant="outline"
            colorScheme="red"
            onClick={handleStop}
            isDisabled={!displayIsRunning}
          >
            Stop
          </Button>
        </HStack>
      </Box>

      <RunProgressPanel
        title="Bulk update progress"
        steps={steps}
        overallProgress={overallProgress}
        isRunning={displayIsRunning}
        currentMessage={policiesProcessStatus?.message ?? ''}
        runSummary={runSummary}
        summaryStats={POLICY_SUMMARY_STATS}
        hasReport={hasReport}
        onDownloadReport={() => dispatch(downloadPoliciesRunReportCsvApi(storeId))}
        isDownloadingReport={downloadPoliciesReportStatus === 'loading'}
        showSummary={!displayIsRunning && runSummary && (runSummary.total ?? 0) > 0}
      />
    </VStack>
  );
};

export default StorePoliciesTab;
